import { useEffect, useState } from 'react';
import { Quest } from '@/network/RestApi';
import { useCharacterStore } from '@/state/characterStore';
import { useUIStore } from '@/state/uiStore';
import npcsData from '@/data/npcs.json';
import { ui } from './theme';
import { CloseButton } from './CloseButton';

const NPC_NAMES: Record<string, string> = (() => {
  const map: Record<string, string> = {};
  for (const n of (npcsData as { npcs: { id: string; name: string }[] }).npcs) {
    map[n.id] = n.name;
  }
  return map;
})();

interface QuestTpl {
  id: number;
  code: string;
  name: string;
  description?: string;
  type: string;
  levelReq: number;
  objectives: { type: string; npc?: string; monsterId?: number; level?: number; count: number }[];
  rewards: { exp?: number; zen?: number; diamond?: number; items?: { tplId: number; qty: number }[] };
  classFilter?: string[];
}

interface OwnedQuest {
  id: number;
  questId: number;
  status: string;
  progress?: Record<string, number>;
}

function displayQuestName(name: string): string {
  return name.replace(/^[A-Z]{1,4}\d+[A-Za-z-]*\s*-\s*/, '');
}

function objectiveLabel(o: QuestTpl['objectives'][number]): string {
  switch (o.type) {
    case 'talk': return `Nói chuyện với ${o.npc ? (NPC_NAMES[o.npc] ?? o.npc) : ''}`;
    case 'kill': return `Tiêu diệt quái #${o.monsterId}`;
    case 'collect_zen': return `Thu thập ${o.count} Zen`;
    case 'reach_level': return `Đạt level ${o.level}`;
    default: return o.type;
  }
}

function questMatchesNpc(q: QuestTpl, npcId: string): boolean {
  if (Array.isArray(q.objectives)) {
    return q.objectives.some((o) => o.npc === npcId);
  }
  return false;
}

export function NpcDialogPanel() {
  const npc = useUIStore((s) => s.npcDialog);
  const close = useUIStore((s) => s.closeNpcDialog);
  const showToast = useUIStore((s) => s.showToast);
  const ch = useCharacterStore((s) => s.current);

  console.log('[NpcDialogPanel] render, npc:', npc, 'open.npcDialog:', useUIStore.getState().open.npcDialog);

  const [quests, setQuests] = useState<QuestTpl[]>([]);
  const [owned, setOwned] = useState<OwnedQuest[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!npc || !ch) return;
    setLoading(true);
    // Tell server we talked to this NPC so any matching talk-objective on
    // accepted quests will progress. Await BEFORE fetching list to avoid a
    // race where the list snapshot is read before the talk write commits.
    Quest.talk(ch.id, { npcId: npc.id })
      .catch(() => { /* ignore - quest may not be accepted yet */ })
      .then(() => Promise.all([
        Quest.templates() as Promise<{ templates: QuestTpl[] }>,
        Quest.list(ch.id) as Promise<{ quests: OwnedQuest[] }>,
      ]))
      .then(([tRes, oRes]) => {
      const ownedList = oRes.quests ?? [];
      const ownedIds = new Set(ownedList.map((q) => q.questId));
      // Show: quests offered by this NPC OR any accepted/completed quest
      // whose talk objective targets this NPC (so player can hand in).
      const available = (tRes.templates ?? []).filter((t) => {
        if (t.levelReq > (ch.level ?? 1)) return false;
        if (t.classFilter && t.classFilter.length > 0 && !t.classFilter.includes(ch.class)) return false;
        if (questMatchesNpc(t, npc.id)) return true;
        return ownedIds.has(t.id) && questMatchesNpc(t, npc.id);
      });
      setQuests(available);
      setOwned(ownedList);
    }).catch((e) => {
      console.error('[NpcDialogPanel] load error:', e);
      setQuests([]);
      setOwned([]);
    }).finally(() => setLoading(false));
  }, [npc, ch]);

  if (!npc) return null;

  const getQuestStatus = (questId: number) => {
    const o = owned.find((q) => q.questId === questId);
    return o?.status ?? null;
  };

  const acceptQuest = async (questId: number) => {
    if (!ch || !npc) return;
    try {
      await Quest.accept(ch.id, { questId });
      // Immediately advance any talk-objective targeting this NPC, since
      // the player is already standing here. Otherwise they'd have to
      // close the dialog and click again.
      await Quest.talk(ch.id, { npcId: npc.id }).catch(() => { /* ignore */ });
      showToast('Nhận nhiệm vụ thành công!');
      const oRes = await Quest.list(ch.id) as { quests: OwnedQuest[] };
      setOwned(oRes.quests ?? []);
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const claimQuest = async (questId: number) => {
    if (!ch) return;
    try {
      await Quest.claim(ch.id, { questId });
      showToast('Nhận thưởng thành công!');
      const oRes = await Quest.list(ch.id) as { quests: OwnedQuest[] };
      setOwned(oRes.quests ?? []);
    } catch (e) {
      showToast((e as Error).message, 'error');
    }
  };

  const openShop = () => {
    close();
    useUIStore.getState().set('shop', true);
  };

  return (
    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: 420, ...ui.panel, zIndex: 100 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <strong style={{ fontSize: 16 }}>{npc.name}</strong>
        <CloseButton onClick={close} />
      </div>

      <div style={{ background: '#0b1220', borderRadius: 4, padding: 10, marginBottom: 12, lineHeight: 1.6 }}>
        {npc.dialog}
      </div>

      {quests.length > 0 && (
        <div>
          <div style={{ fontWeight: 600, marginBottom: 6 }}>Nhiệm vụ</div>
          {loading && <div style={{ opacity: 0.6 }}>Đang tải...</div>}
          {quests.map((q) => {
            const status = getQuestStatus(q.id);
            const ownedRow = owned.find((o) => o.questId === q.id);
            return (
              <div key={q.id} style={{ padding: 8, background: '#111a2b', borderRadius: 4, marginBottom: 6 }}>
                <div style={{ fontWeight: 600 }}>{displayQuestName(q.name)}</div>
                {q.description && (
                  <div style={{ fontSize: 12, opacity: 0.85, margin: '4px 0' }}>{q.description}</div>
                )}
                <div style={{ fontSize: 12, opacity: 0.7 }}>Level yêu cầu: {q.levelReq}</div>
                {(q.objectives?.length ?? 0) > 0 && (
                  <ul style={{ margin: '4px 0 0 16px', padding: 0, fontSize: 12 }}>
                    {q.objectives.map((o, i) => {
                      const cur = ownedRow?.progress?.[`obj_${i}`] ?? 0;
                      const done = cur >= o.count;
                      return (
                        <li key={i} style={{ color: done ? '#66bb6a' : '#dce3ec' }}>
                          {objectiveLabel(o)} ({cur}/{o.count})
                        </li>
                      );
                    })}
                  </ul>
                )}
                {q.rewards && (
                  <div style={{ fontSize: 12, color: '#ffd166', marginTop: 4 }}>
                    Thưởng: {q.rewards.exp ? `${q.rewards.exp} EXP ` : ''}{q.rewards.zen ? `${q.rewards.zen} Zen ` : ''}{q.rewards.diamond ? `${q.rewards.diamond} KC` : ''}
                  </div>
                )}
                <div style={{ marginTop: 6 }}>
                  {!status && (
                    <button style={ui.primaryBtn} onClick={() => acceptQuest(q.id)}>Nhận nhiệm vụ</button>
                  )}
                  {status === 'accepted' && (
                    <span style={{ color: '#4cd1ff', fontSize: 13 }}>Đang thực hiện...</span>
                  )}
                  {status === 'completed' && (
                    <button style={ui.primaryBtn} onClick={() => claimQuest(q.id)}>Nhận thưởng</button>
                  )}
                  {status === 'claimed' && (
                    <span style={{ color: '#66bb6a', fontSize: 13 }}>Đã hoàn thành</span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {npc.role === 'shop' && (
        <button style={ui.primaryBtn} onClick={openShop}>Mở cửa hàng</button>
      )}

      {npc.role === 'storage' && (
        <button style={ui.primaryBtn} onClick={() => { close(); useUIStore.getState().set('storage', true); }}>
          Mở kho cá nhân
        </button>
      )}

      {npc.role === 'guild' && (
        <button style={ui.primaryBtn} onClick={() => { close(); useUIStore.getState().set('guild', true); }}>
          Mở Guild
        </button>
      )}
    </div>
  );
}
